import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import { orderBy } from "../../../common/utils.ts";

export interface NotableAchievement {
	statName: string;
	statKey: string;
	pid: number;
	name: string;
	abbrev: string;
	tid: number;
	newRank: number;
	oldRank: number;
	newValue: number;
	passedPlayer?: {
		pid: number;
		name: string;
		abbrev: string;
		tid: number;
		value: number;
		rank: number;
	};
}

export const getNotableAchievements = async (
	season: number,
): Promise<NotableAchievement[]> => {
	// Feature specifically for basketball
	if (__SPORT !== "basketball") {
		return [];
	}

	const statsToTrack = [
		{ key: "pts", name: "Points" },
		{ key: "trb", name: "Rebounds" },
		{ key: "ast", name: "Assists" },
		{ key: "blk", name: "Blocks" },
		{ key: "stl", name: "Steals" },
		{ key: "tp", name: "3-pointers" },
		{ key: "gp", name: "Games Played" },
	];

	// Fetch all players using 'noCopyCache' for high performance
	const allPlayers = await idb.getCopies.players({}, "noCopyCache");

	const achievements: NotableAchievement[] = [];

	for (const stat of statsToTrack) {
		const playersWithStats: {
			pid: number;
			name: string;
			tid: number;
			abbrev: string;
			totalS: number;
			totalPrev: number;
			playedInS: boolean;
		}[] = [];

		for (const p of allPlayers) {
			let totalS = 0;
			let totalPrev = 0;
			let playedInS = false;
			let lastTid = p.tid;

			for (const row of p.stats) {
				if (row.playoffs === false) {
					const val = (row as any)[stat.key] ?? 0;
					if (row.season <= season) {
						totalS += val;
						if (row.season === season) {
							playedInS = true;
							lastTid = row.tid;
						}
					}
					if (row.season <= season - 1) {
						totalPrev += val;
					}
				}
			}

			if (totalS > 0) {
				const teamInfo = g.get("teamInfoCache")[lastTid];
				const abbrev = teamInfo ? teamInfo.abbrev : "???";

				playersWithStats.push({
					pid: p.pid,
					name: `${p.firstName} ${p.lastName}`,
					tid: lastTid,
					abbrev,
					totalS,
					totalPrev,
					playedInS,
				});
			}
		}

		// Sort to compute ranks at S and S - 1
		const sortedS = orderBy(playersWithStats, (p) => p.totalS, "desc");
		const sortedPrev = orderBy(playersWithStats, (p) => p.totalPrev, "desc");

		const prevRankMap = new Map<number, number>();
		sortedPrev.forEach((p, idx) => {
			prevRankMap.set(p.pid, idx + 1);
		});

		sortedS.forEach((p, idx) => {
			const newRank = idx + 1;
			const oldRank = prevRankMap.get(p.pid) ?? Infinity;

			// Check if they climbed rankings and landed in top 50 all-time
			if (p.playedInS && newRank < oldRank && newRank <= 50) {
				let passedPlayer;
				const passedIdx = idx + 1;
				if (passedIdx < sortedS.length) {
					const passed = sortedS[passedIdx]!;
					passedPlayer = {
						pid: passed.pid,
						name: passed.name,
						tid: passed.tid,
						abbrev: passed.abbrev,
						value: passed.totalS,
						rank: passedIdx + 1,
					};
				}

				achievements.push({
					statName: stat.name,
					statKey: stat.key,
					pid: p.pid,
					name: p.name,
					tid: p.tid,
					abbrev: p.abbrev,
					newRank,
					oldRank,
					newValue: p.totalS,
					passedPlayer,
				});
			}
		});
	}

	// Sort so the highest rank achievements are displayed first
	return orderBy(achievements, (a) => a.newRank, "asc");
};
